import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const SnackbarContext = createContext(null);

const DEFAULT_AUTO_HIDE_DURATION = 4000;

export const SnackbarProvider = ({ children }) => {
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info',
        autoHideDuration: DEFAULT_AUTO_HIDE_DURATION,
    });

    const closeSnackbar = useCallback((event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    const showSnackbar = useCallback((message, options = {}) => {
        setSnackbar({
            open: true,
            message,
            severity: options.severity || 'info',
            autoHideDuration: options.autoHideDuration || DEFAULT_AUTO_HIDE_DURATION,
        });
    }, []);

    const value = useMemo(() => ({
        showSnackbar,
        success: (message, options = {}) => showSnackbar(message, { ...options, severity: 'success' }),
        error: (message, options = {}) => showSnackbar(message, { ...options, severity: 'error' }),
        warning: (message, options = {}) => showSnackbar(message, { ...options, severity: 'warning' }),
        info: (message, options = {}) => showSnackbar(message, { ...options, severity: 'info' }),
    }), [showSnackbar]);

    return (
        <SnackbarContext.Provider value={value}>
            {children}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={snackbar.autoHideDuration}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);

    if (!context) {
        throw new Error('useSnackbar must be used within SnackbarProvider');
    }

    return context;
};
